<template>
  <div class="relative w-full">
    <input v-bind="$attrs" :value="inputValue" :placeholder="placeholder" :type="type"
      class="h-11 w-full rounded-[var(--radius-md)] border bg-card px-3.5 text-[15px] text-foreground placeholder:text-muted-foreground outline-none transition-shadow focus:ring-2 focus:ring-offset-0"
      :class="[ringClass, suffix ? 'ps-8' : '']" @input="onInput"
      @blur="handleBlur" />
    <span v-if="suffix"
      class="absolute top-1/2 end-3 -translate-y-1/2 text-[13px] text-primary-black pointer-events-none select-none">
      {{ suffix }}
    </span>
  </div>
  <ErrorMessage v-if="name" :name="name" />
</template>

<script setup lang="ts">
import { useField } from 'vee-validate'
import { computed } from 'vue'

const props = defineProps<{
  name?: string
  modelValue?: string
  invalid?: boolean
  valid?: boolean
  label?: string
  placeholder?: string
  type?: string
  disabled?: boolean
  suffix?: string
  numeric?: boolean
  max?: number
  maxDecimals?: number
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const { value: fieldValue, handleBlur } = useField<string>(() => props.name || '', undefined, {
  initialValue: props.modelValue || '',
  syncVModel: true,
})

const inputValue = computed({
  get: () => (props.name ? fieldValue.value : props.modelValue || ''),
  set: (value: string) => {
    if (props.name) {
      fieldValue.value = value
    }

    emit('update:modelValue', value)
  },
})
function toEnglishDigits(str: string) {
  return str
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
    .replace(/٫/g, '.')
}

function sanitizeNumeric(raw: string, prev: string) {
  let v = toEnglishDigits(raw).replace(/[^\d.]/g, '')
  const [intPart, ...rest] = v.split('.')
  if (rest.length) {
    v = intPart + '.' + rest.join('').slice(0, props.maxDecimals ?? 2)
  }
  v = v.replace(/^0+(?=\d)/, '')
  if (props.max !== undefined && v !== '' && Number(v) > props.max) {
    return prev
  }

  return v
}
function onInput(event: Event) {
  const el = event.target as HTMLInputElement
  const next = props.numeric ? sanitizeNumeric(el.value, inputValue.value) : el.value
  inputValue.value = next
  if (el.value !== next) el.value = next
}

const ringClass = computed(() => {
  if (props.invalid) return 'border-[var(--danger)] focus:ring-[var(--danger)]'
  if (props.valid) return 'border-[var(--success)] focus:ring-[var(--success)]'
  return 'border-border focus:ring-primary'
})
</script>
